import { bookingServices } from "../../../mocks/bookingData";
import type { BookingState } from "../page";

interface Step1ServiceProps {
  booking: BookingState;
  onUpdate: (updates: Partial<BookingState>) => void;
  onNext: () => void;
}

const serviceIcons: Record<string, string> = {
  "rubbish-clearance": "ri-truck-line",
  "rubbish-removal": "ri-delete-bin-2-line",
  "sofa-removal": "ri-sofa-line",
  "private-bin-collection": "ri-delete-bin-line",
  "furniture-removal": "ri-archive-line",
  "house-clearance": "ri-home-2-line",
  "appliance-removal": "ri-plug-line",
  "commercial-waste-clearance": "ri-building-2-line",
  "office-clearance": "ri-computer-line",
  "site-clearance": "ri-hammer-line",
  "retail-clearance": "ri-store-2-line",
  "contract-bin-collections": "ri-calendar-check-line",
  "bulk-waste-removal": "ri-truck-line",
};

export default function Step1Service({ booking, onUpdate, onNext }: Step1ServiceProps) {
  const handleSelect = (slug: string) => {
    const service = bookingServices.find((s) => s.slug === slug);
    if (!service) return;
    onUpdate({
      serviceSlug: service.slug,
      serviceTitle: service.title,
      stepType: service.stepType,
      selectedOption: "",
      selectedOptionLabel: "",
      selectedOptionPrice: 0,
      cartItems: [],
    });
    onNext();
  };

  return (
    <div className="w-full">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-4 thick-border cartoon-shadow-sm">
          <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Step 1 of 3</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-cl-dark mb-3">Select your service</h1>
        <p className="text-cl-dark/60 font-semibold">Choose the service you need - fixed prices, no hidden extras.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {bookingServices.map((service) => (
          <button
            key={service.slug}
            onClick={() => handleSelect(service.slug)}
            style={{ touchAction: "manipulation" }}
            className={`group relative bg-white rounded-2xl overflow-hidden text-left transition-all cursor-pointer cartoon-card hover:-translate-y-1 ${
              booking.serviceSlug === service.slug ? "ring-4 ring-cl-green ring-offset-2" : ""
            }`}
          >
            <div className="relative border-b-2 border-cl-dark h-48 overflow-hidden">
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 pointer-events-none"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
            </div>

            <div className="p-5">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-cl-lime border border-cl-green text-cl-green flex-shrink-0">
                  <i className={`${serviceIcons[service.slug] ?? "ri-service-line"} text-sm`}></i>
                </span>
                <h3 className="font-black text-cl-dark text-base">{service.title}</h3>
              </div>
              <p className="text-sm text-gray-500 mb-5 font-semibold pl-9">{service.priceFrom} inc. VAT</p>

              <div className="bg-cl-dark text-white text-sm font-black py-3 rounded-xl text-center w-full cartoon-btn transition-all group-hover:bg-cl-green">
                Select
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-10 bg-white rounded-2xl px-6 py-4 cartoon-card flex flex-wrap items-center justify-center gap-6">
        {[
          { icon: "ri-shield-check-line", text: "Fully licensed (CBDU 634440)" },
          { icon: "ri-star-fill", text: "4.8 stars — 200+ reviews" },
          { icon: "ri-leaf-line", text: ">95% diverted from landfill" },
          { icon: "ri-file-text-line", text: "Waste transfer note included" },
        ].map((item) => (
          <div key={item.text} className="flex items-center gap-2">
            <i className={`${item.icon} text-cl-green text-sm`}></i>
            <span className="text-xs font-bold text-cl-dark">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}